import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Home, Building2, Car, Bike, UtensilsCrossed, Wrench, ArrowLeft, ArrowRight, CheckCircle2,
} from 'lucide-react';

/**
 * Expand-account page. A single landing spot for "I'm already a guest,
 * how do I also become a host / driver / landlord / rider / restaurant
 * partner / service provider?"
 *
 * Backed by the fact that every role-specific signup endpoint
 * (delivery, drivers, restaurants, marketplace/landlord, providers,
 * complete-host-onboarding) detects an existing user and $addToSet the
 * new role, so the user never ends up with a duplicate account — the
 * UI just needs to surface the path.
 *
 * Roles the user already has are shown as ticked / disabled so they
 * can see their full account composition at a glance.
 */

const ALL_ROLES = [
  {
    key: 'host',
    label: 'Host',
    blurb: 'List a short stay property and earn from London visitors.',
    icon: Home,
    path: '/become-host',
    color: 'from-[#1F3A68] to-[#1F3A68]/80',
  },
  {
    key: 'landlord',
    label: 'Landlord',
    blurb: 'Rent out a long-let property. Tenants find you through Ariv.',
    icon: Building2,
    path: '/landlord/signup',
    color: 'from-emerald-600 to-emerald-700',
  },
  {
    key: 'driver',
    label: 'Taxi Driver',
    blurb: 'Drive on the Ariv taxi network. UK compliance handled inside.',
    icon: Car,
    path: '/driver/signup',
    color: 'from-amber-600 to-amber-700',
  },
  {
    key: 'delivery_rider',
    label: 'Delivery Rider',
    blurb: 'Deliver food orders by bike, moped, or car around the city.',
    icon: Bike,
    path: '/delivery/signup',
    color: 'from-rose-600 to-rose-700',
  },
  {
    key: 'restaurant_partner',
    label: 'Restaurant Partner',
    blurb: 'List your restaurant for orders and table bookings.',
    icon: UtensilsCrossed,
    path: '/restaurant/signup',
    color: 'from-orange-600 to-orange-700',
  },
  {
    key: 'service_provider',
    label: 'Service Provider',
    blurb: 'Offer home services: cleaning, repairs, gardening, and more.',
    icon: Wrench,
    path: '/provider/signup',
    color: 'from-purple-600 to-purple-700',
  },
];

const AddRolePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated()) {
      navigate('/login?next=/add-role', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1F3A68]" />
      </div>
    );
  }

  const userRoles = new Set(user.roles || []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-5xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-[#1F3A68] hover:underline mb-6"
        >
          <ArrowLeft size={18} /> Back
        </button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-2">
            Expand your account
          </h1>
          <p className="text-gray-600 max-w-2xl">
            Your Ariv login can do more than book stays. Pick anything else you want to do —
            you'll go through a quick role-specific setup and your existing account picks up
            the new role. No second account needed.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ALL_ROLES.map(({ key, label, blurb, icon: Icon, path, color }) => {
            const alreadyHas = userRoles.has(key);
            return (
              <Card
                key={key}
                className={`overflow-hidden transition-shadow ${alreadyHas ? 'opacity-70' : 'hover:shadow-xl'}`}
              >
                <div className={`bg-gradient-to-br ${color} p-5 text-white`}>
                  <div className="flex items-center justify-between">
                    <Icon size={32} />
                    {alreadyHas && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/20 px-2 py-1 rounded-full">
                        <CheckCircle2 size={14} /> Active
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mt-3">{label}</h3>
                </div>
                <CardContent className="p-5">
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{blurb}</p>
                  {alreadyHas ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled
                    >
                      Already on your account
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate(path)}
                      className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                    >
                      Start setup <ArrowRight size={16} className="ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-10 p-5 bg-white rounded-xl border border-gray-200">
          <p className="text-sm text-gray-700">
            <strong>One login, every role.</strong> All your activity, payouts, messages and
            verifications stay tied to <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{user.email}</code>.
            You can switch between modes from the menu at the top right at any time.
          </p>
          <Link to="/settings" className="text-sm text-[#1F3A68] hover:underline inline-block mt-2">
            Manage account settings →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AddRolePage;
